import React, { useEffect, useState, useCallback } from 'react'
import { DeletedUserRow } from '@monorepo/interfaces'
import {
  formatDateToPST,
  http,
  isDate,
  isNonEmptyString,
} from '@monorepo/infra'
import * as S from '../styles'
import { PageSpinner } from '@monorepo/components'
import * as FileSaver from 'file-saver'
import XLSX from 'sheetjs-style'
import moment from 'moment'

interface DeletedUsersProps {
  startDate: Date
  endDate: Date
}

const DeletedUsers: React.FC<DeletedUsersProps> = ({ startDate, endDate }) => {
  const [report, setReport] = useState<DeletedUserRow[] | null>(null)

  useEffect(() => {
    http
      .get<DeletedUserRow[]>({
        url: `/reports/deletedusers?dateFrom=${encodeURIComponent(
          moment(startDate).startOf('day').toISOString(true)
        )}&dateTo=${encodeURIComponent(
          moment(endDate).endOf('day').toISOString(true)
        )}`,
      })
      .then(({ data }) => {
        data.forEach((item) => {
          if (
            !isDate(item.date) &&
            isNonEmptyString(item.date as unknown as string)
          ) {
            const date = new Date(item.date as unknown as string)

            if (isDate(date)) {
              item.date = date
            }
          }
        })
        setReport(data)
      })
  }, [endDate, startDate])

  const exportExcel = useCallback(async () => {
    if (report !== null) {
      const reportData = report.map((item) => ({
        Date: item.date,
        Deleter: item.deleter,
        Deleted: item.deleted,
      }))
      const fileType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset-UTF-8'
      const fileExtension = '.xlsx'
      const ws = XLSX.utils.json_to_sheet(reportData)
      const wb = { Sheets: { Sheet1: ws }, SheetNames: ['Sheet1'] }
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const data = new Blob([excelBuffer], { type: fileType })
      FileSaver.saveAs(data, `DeletedUsers${fileExtension}`)
    }
  }, [report])

  if (!report || report === null) {
    return <PageSpinner />
  }

  return (
    <S.Container>
      <S.ReportTitle>
        Deleted users
        <S.ExcelButton
          label="Download Excel"
          colorOption="blue"
          onClick={exportExcel}
        />
      </S.ReportTitle>
      <S.FilterList>
        <S.FilterListItem>
          <S.FilterListItemTitle>Time Period</S.FilterListItemTitle>
          <S.FilterListItemValue>
            {`${formatDateToPST(startDate)} - ${formatDateToPST(endDate)}`}
          </S.FilterListItemValue>
          <S.StyledIcon icon="FourPlusRectangle" />
        </S.FilterListItem>
      </S.FilterList>
      {report.length > 0 && (
        <>
          <S.TableContainer>
            <S.Table>
              <S.TableHead>
                <S.TableRow>
                  <S.TableHeadCell>Date</S.TableHeadCell>
                  <S.TableHeadCell>Deleter</S.TableHeadCell>
                  <S.TableHeadCell>Deleted</S.TableHeadCell>
                </S.TableRow>
              </S.TableHead>
              <S.TableBody>
                {report.map((item: DeletedUserRow, index: number) => (
                  <S.TableRow key={`row${index}`}>
                    <S.TableCell>
                      {formatDateToPST(item.date as Date)}
                    </S.TableCell>
                    <S.TableCell>{item.deleter}</S.TableCell>
                    <S.TableCell>{item.deleted}</S.TableCell>
                  </S.TableRow>
                ))}
              </S.TableBody>
            </S.Table>
          </S.TableContainer>
        </>
      )}
    </S.Container>
  )
}

export default DeletedUsers
